"use client";

import React, { useState } from "react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { ArrowRight, Loader, TrendingUp } from "lucide-react";
function CaseStudiesGallery() {
  const {
    posts,
    loading,
    error,
    hasNext,
    loadMore,
    isRefetching
  } = usePosts();
  const [filter, setFilter] = useState("all");
  const categories = [{
    id: "all",
    label: "All Events"
  }, {
    id: "seo",
    label: "Weddings"
  }, {
    id: "strategy",
    label: "Corporate"
  }, {
    id: "visual",
    label: "Private Soirées"
  }];
  if (loading && posts.length === 0) {
    return <div className="flex min-h-[400px] items-center justify-center">
        <Loader className="size-10 animate-spin text-primary" />
      </div>;
  }
  if (error) {
    return <div className="py-20 text-center text-destructive">
        <p>Failed to load our gallery. Please try again later.</p>
      </div>;
  }
  const filteredPosts = filter === "all" ? posts : posts.filter((_, i) => {
    if (filter === "seo") return i % 3 === 0;
    if (filter === "strategy") return i % 3 === 1;
    if (filter === "visual") return i % 3 === 2;
    return true;
  });
  return <div className="space-y-12">
      {}
      <div className="flex justify-center">
        <ToggleGroup type="single" value={filter} onValueChange={value => value && setFilter(value)} className="flex flex-wrap justify-center gap-2 rounded-md bg-secondary/50 p-1">
          {categories.map((cat, i) => <ToggleGroupItem key={cat.id} value={cat.id} data-index={i} className="rounded-md px-6 py-2 text-sm font-default tracking-widest uppercase transition-all data-[state=on]:bg-primary data-[state=on]:text-primary-foreground data-[state=on]:shadow-md hover:bg-accent">
              {cat.label}
            </ToggleGroupItem>)}
        </ToggleGroup>
      </div>

      {}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:gap-8">
        {filteredPosts.map((post, i) => <div key={post.id} data-index={i}>
            <Link to={post.link || "#"} className="group relative block aspect-[4/3] w-full overflow-hidden rounded-lg bg-muted shadow-lg">
              {}
              {post.featuredImage ? <img src={post.featuredImage} alt={post.title} className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" /> : <div className="flex h-full w-full items-center justify-center bg-muted-foreground/10">
                  <span className="text-muted-foreground font-serif">Signature Occasion</span>
                </div>}

              {}
              <div className="absolute inset-x-0 bottom-0 flex flex-col items-center justify-center bg-white/40 backdrop-blur-md p-8 text-center opacity-0 transition-all duration-500 ease-in-out group-hover:opacity-100">
                <div className="translate-y-4 transition-transform duration-500 ease-out group-hover:translate-y-0">
                  {}
                  <div className="mb-4 inline-flex items-center gap-1.5 rounded-md bg-primary/10 px-4 py-1.5 text-xs font-default tracking-widest uppercase text-primary">
                    <TrendingUp className="size-4" />
                    {post.customFields?.results || "Exquisite Detail"}
                  </div>
                  
                  <h3 className="font-serif mb-4 text-2xl font-medium text-primary md:text-3xl lg:text-4xl">
                    {post.title}
                  </h3>
                  
                  <p className="mb-8 max-w-md text-foreground/80 font-default text-sm tracking-wide md:text-base">
                    {post.excerpt ? post.excerpt.replace(/(<([^>]+)>)/gi, "").substring(0, 120) + "..." : "A bespoke celebration curated with ethereal elegance and timeless sophistication for our discerning clients."}
                  </p>
                  
                  <div className="inline-flex items-center gap-2 border-b border-primary/40 pb-1 font-default text-xs tracking-widest uppercase text-primary transition-colors hover:border-primary">
                    Explore Event <ArrowRight className="size-4" />
                  </div>
                </div>
              </div>
            </Link>
          </div>)}
      </div>

      {}
      {hasNext && <div className="flex justify-center pt-8">
          <Button size="lg" variant="outline" onClick={async () => await loadMore()} disabled={isRefetching} className="min-w-[200px] rounded-md border-primary/30 text-primary font-default tracking-widest uppercase hover:bg-primary hover:text-primary-foreground transition-all duration-300">
            {isRefetching ? <>
                <Loader className="mr-2 size-4 animate-spin" /> Loading...
              </> : "View More Occasions"}
          </Button>
        </div>}
    </div>;
}
export default function OurGalleryOfExcellence() {
  const wp_query_params = {
    post_type: "post",
    per_page: 6
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section data-section-id="234270" id="our-gallery-of-excellence" className="bg-background text-foreground px-4 py-24 md:px-8 lg:px-12">
      <div className="mx-auto max-w-7xl">
        <div className="mb-16 text-center">
          <h2 className="font-serif mb-6 text-4xl font-light tracking-tight md:text-5xl lg:text-6xl">
            Our <span className="italic text-primary">Gallery</span> of Excellence
          </h2>
          <p className="mx-auto max-w-2xl text-lg text-muted-foreground font-default tracking-wide md:text-xl">
            A curated collection of bespoke weddings, corporate galas, and intimate private soirées.
          </p>
        </div>

        <PostsProvider wp_query={{
        post_type: "post",
        per_page: 6
      }}>
          <CaseStudiesGallery />
        </PostsProvider>
      </div>
    </section>;
}